/**
 * Pottery painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const potteryPaintingIdeasPack: ToilePack = {
    slug: 'pottery-painting-ideas',
    category: 'craft',
    name: 'Pottery painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'mug', label: 'Mug', subject: 'a mug painted with cherries and a checker rim'},
        {key: 'plate', label: 'Plate', subject: 'a plate painted with lemons'},
        {key: 'bowl', label: 'Bowl', subject: 'a bowl with stripes and dots'},
    ],
    styles: [
        {key: 'bisque', label: 'Before firing', style: 'freshly painted and not yet fired, the colours chalky and matte, on a cafe table beside little pots of underglaze and brushes in a jar', palette: 'Pale bisque white and soft chalky underglaze colours, the table warm wood.', framing: 'Shot at a slight high angle, the piece centred with the paint pots to one side.'},
        {key: 'glazed', label: 'After firing', style: 'after the kiln, glossy and glazed, the colours deep and bright, set on a white linen cloth', palette: 'Clean white linen, the fired colours rich with a soft glaze shine.', framing: 'Shot at a three-quarter angle, the piece isolated in the centre.'},
        {key: 'kitchen', label: 'In use', style: 'in use in a home kitchen with fruit or coffee, on a wooden counter beside a tea towel', palette: 'Warm wood and cream kitchen tones around the painted colours.', framing: 'Shot at counter level, the piece in the foreground and the kitchen softly blurred behind.'},
    ],
    directions: ['strawberries', 'pet portrait', 'blue willow'],
    directionHint: 'try: strawberries',
    seo: {
        title: 'Pottery painting ideas: mug, plate, bowl',
        metaDescription: 'Nine pottery painting ideas: a cherry mug, a lemon plate and a striped bowl, before firing, after firing and in use.',
        h1: 'Pottery painting ideas: mug, plate, bowl',
        tagline: 'Three classic pieces, each shown before firing, after firing and in use at home.',
        intro: 'Pottery painting studios let you paint a piece before it is fired in a kiln. These nine show three designs: a mug with cherries and a checker rim, a plate with lemons, and a bowl with stripes and dots. Each piece appears freshly painted, after firing in a glaze, and in use in a kitchen. Type a design, such as strawberries or blue willow, to ask for it.',
        useCases: [
            'a mug or bowl for the kitchen',
            'a plate as a gift',
            'a studio class or birthday activity',
            'a matching set of pieces',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A mug with cherries, a plate with lemons and a bowl with stripes and dots. Each is shown before firing, after firing and in use.'},
            {q: 'Are these photos of studio pieces?', a: 'No. They are generated pictures of each design, not photographs of pieces from a particular studio.'},
            {q: 'What is the difference before and after firing?', a: 'Before firing the paint is chalky and matte. After firing the glaze makes the colours deeper and glossy.'},
            {q: 'Can I ask for a different design?', a: 'Yes. Type a design such as strawberries, a pet portrait or blue willow. A typed design is a request, not a promised result.'},
        ],
    },
}
